import React, { memo } from 'react'
import tw from 'twin.macro'
import { PartnerFilterSelect } from 'src/components/payments-ops/PartnerFilterSelect'
import { ToolbarCheckbox } from 'src/components/payments-ops/ToolbarCheckbox'
import { ToolbarRow } from 'src/components/payments-ops/ToolbarRow'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { TableSearch } from 'src/components/shared/table/TableSearch'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'

interface Props {
  partnerId: string | null
  externalTransactionId: string
  activeOnly: boolean
  onPartnerChange(partnerId: string | null): void
  onExternalTransactionIdChange(value: string): void
  onActiveOnlyChange(activeOnly: boolean): void
  onClear(): void
  onRefresh(): void
}

const IncomingTransactionsToolbarComponent = ({
  partnerId,
  externalTransactionId,
  activeOnly,
  onPartnerChange,
  onExternalTransactionIdChange,
  onActiveOnlyChange,
  onClear,
  onRefresh,
}: Props) => {
  // The options come from `GET /partners` (`partners_view`), which PaymentsOpsLayout skips without
  // it — an "All partners"-only select is useless, so the filter is hidden (spec §6).
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)

  return (
    <ToolbarRow>
      {canViewPartners ? (
        <PartnerFilterSelect value={partnerId} onChange={onPartnerChange} />
      ) : null}
      {/* Exact match on the backend — a server search, so the debounce is the default 700ms. */}
      <TableSearch
        searchQuery={externalTransactionId}
        onChangeSearchQuery={onExternalTransactionIdChange}
        width={260}
        inputDataTestId="incoming-external-id-search"
      />
      <ToolbarCheckbox
        name="incomingActiveOnly"
        label="Active partners only"
        checked={activeOnly}
        onChange={onActiveOnlyChange}
      />
      <div css={tw`ml-auto flex flex-row gap-2 pb-1`}>
        <Button size="small" color="grey" onClick={onClear}>
          Clear
        </Button>
        <Button
          size="small"
          color="white"
          leftIcon={<GSIcon iconName="gs_arrows_repeat" size={20} css={tw`text-shades-1000`} />}
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </div>
    </ToolbarRow>
  )
}

export const IncomingTransactionsToolbar = memo(IncomingTransactionsToolbarComponent)
